import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ProblemService } from '@/services/problem.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@/services/auth.service';
import { map, combineLatest, Subject, startWith, defer, Observable, filter } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { getAttachedByText } from '@/util';
import { Label, LabelAnnotation } from '@/types';

type SelectedLabelsForm = FormGroup<{
    problemId: FormControl<number>;
    selectedLabelIds: FormControl<number[]>;
}>;

export type ManageLabelsModalResult = ReturnType<SelectedLabelsForm['getRawValue']>;

@Component({
    selector: 'la-manage-labels-modal',
    imports: [ReactiveFormsModule, AsyncPipe],
    templateUrl: './manage-labels-modal.component.html',
    styleUrl: './manage-labels-modal.component.scss'
})
export class ManageLabelsModalComponent implements OnInit {
    private problemService = inject(ProblemService);
    private authService = inject(AuthService);
    private destroyRef = inject(DestroyRef);

    public form: SelectedLabelsForm = new FormGroup({
        problemId: new FormControl<number>(-1, { nonNullable: true }),
        selectedLabelIds: new FormControl<number[]>([], {
            nonNullable: true,
        }),
    });

    /** Takes a Label ID and adds it to selected */
    private addLabelId$ = new Subject<number>();

    public currentAnnotations: LabelAnnotation[] = [];

    public allLabels$ = this.problemService.allLabels$;

    // Defer ensures that the form has the correct values before we subscribe to valueChanges.
    public selectedLabelIds$ = defer(() => this.form.controls.selectedLabelIds.valueChanges.pipe(
        startWith(this.form.controls.selectedLabelIds.value),
    ));

    public allLabelsAndSelectedIds = combineLatest([
        this.allLabels$.pipe(filter(labels => labels.length > 0)),
        this.selectedLabelIds$
    ]);

    /**
     * Turns selected label IDs into objects that are used in the template.
    */
    public shownLabels$: Observable<LabelAnnotation[]> = this.allLabelsAndSelectedIds.pipe(
        map(([allLabels, selectedIds]) => this.convertSelectedIdsToAnnotations(selectedIds, allLabels)),
    );

    public availableLabels$ = this.allLabelsAndSelectedIds.pipe(
        map(([allLabels, selectedIds]) => allLabels.filter(label => !selectedIds.includes(label.id))),
    );

    public loadingLabels$ = this.availableLabels$.pipe(
        map(() => false),
        startWith(true)
    );

    constructor(public activeModal: NgbActiveModal) { }

    ngOnInit(): void {
        // Handle add label operations
        this.addLabelId$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((labelId) => {
            const currentSelected = this.form.controls.selectedLabelIds.value;
            if (!currentSelected.includes(labelId)) {
                this.form.controls.selectedLabelIds.setValue([...currentSelected, labelId]);
            }
        });
    }

    public removeLabel(labelId: number): void {
        const currentSelected = this.form.controls.selectedLabelIds.value;
        this.form.controls.selectedLabelIds.setValue(
            currentSelected.filter(id => id !== labelId)
        );
    }

    public addLabelAnnotation(labelId: number): void {
        this.addLabelId$.next(labelId);
    }

    public closeModal(): void {
        const formValue = this.form.getRawValue();
        // Transform selectedLabelIds to the expected backend format
        const transformedValue = {
            problemId: formValue.problemId,
            selectedLabels: formValue.selectedLabelIds.map(id => ({ id }))
        };
        this.activeModal.close(transformedValue);
    }

    public getAttachedBy = getAttachedByText;

    private currentUserName = toSignal(
        this.authService.currentUser$.pipe(map((user) => user?.username))
    );

    private convertSelectedIdsToAnnotations(selectedIds: number[], allLabels: Label[]): LabelAnnotation[] {
        const existingAnnotationMap = new Map(
            this.currentAnnotations.map(a => [a.label.id, a])
        );
        const allLabelsMap = new Map(allLabels.map(l => [l.id, l]));
        const currentUser = this.currentUserName() ?? $localize`Unknown user`;

        return selectedIds.map(labelId => {
            // Try to find existing annotation first
            const existing = existingAnnotationMap.get(labelId);
            if (existing) {
                return existing;
            }

            return this.createAnnotationForId(labelId, allLabelsMap, currentUser);
        }).filter(annotation => annotation !== null);
    }

    private createAnnotationForId(labelId: number, allLabels: Map<number, Label>, currentUser: string): LabelAnnotation | null {
        const label = allLabels.get(labelId);
        if (!label) {
            return null;
        }

        return {
            id: null,
            label,
            createdAt: new Date().toISOString(),
            createdBy: currentUser,
            attachedByCurrentUser: true,
            session: null,
            removedAt: null,
            removedBy: null,
            notes: '',
            removable: true,
        } as LabelAnnotation;
    }
}
